import { CustomerAppDescription } from '../customerAppIntegration/customerAppDescription';
import { CustomerAppError } from '../customerAppIntegration/customerAppError';
import { DsiInspectionResult } from '../dsi/dsiInspection';
import { ExternalPayment, Payment } from '../payment/payment';
import { RetainMarker } from '../retainMarker';
import { ClassificationDetection } from '../snapshot/classificationDetection';
import { CustomerTicket } from '../ticket/ticket';
import { WeightWithoutScanModel } from '../weightWithoutScan/notification';
import { BasketItem } from './basketItem';
import { BottleBundleCheck } from './bottleBundleCheck';
import { SessionCustomerInfo } from './sessionCustomerInfo';
import { SessionLocation } from './sessionLocation';
import { SessionMonitorSessionDeviceMetadata } from './sessionMonitor';
import { z } from 'zod';

export type SessionImageDetectionImage = z.infer<typeof SessionImageDetectionImage>;
export type SessionImageDetectionConfirmation = z.infer<typeof SessionImageDetectionConfirmation>;
export type SessionImageDetection = z.infer<typeof SessionImageDetection>;

export const SessionImageDetectionImage = z.object({
    encodedImage: z.string().nullish(),
    mimeType: z.string().nullish(),
    width: z.number().nullish(),
    height: z.number().nullish(),
    timestamp: z.coerce.date().nullish(),
    path: z.string().nullish(),
    host: z.string().nullish(),
});

export const SessionImageDetection = z.object({
    detection: ClassificationDetection,
    image: SessionImageDetectionImage.nullish(),
    detectionId: z.string().nullish(),
});

export const SessionImageDetectionConfirmation = SessionImageDetection.extend({
    confirmation: DsiInspectionResult.nullish(),
    confirmedOn: z.coerce.date().nullish(),
    confirmedBy: z
        .object({
            accountGuid: z.string().nullish(),
            userName: z.string().nullish(),
        })
        .nullish(),
});

export const SessionSnapshotImage = z.object({
    ts: z.coerce.date(),
    logicalPaths: z.string().array().nullish(),
    correlationGuid: z.string(),
    trigger: z.string(),
});

export const SessionManualCheck = z.object({
    checkType: z.string(),
    createdBy: z.string().nullish(),
    createdOn: z.string().nullish(),
    forced: z.boolean().nullish(),
    modifiedBy: z.string().nullish(),
    modifiedOn: z.string().nullish(),
});

export const SessionPreparationState = z.enum(['scanned', 'pending', 'banner']);
export type SessionPreparationState = z.infer<typeof SessionPreparationState>;

export const SessionFlags = z.object({
    scanNotResolvable: z.string().nullish(),
    scanFailureReason: z.string().nullish(),
    scanNotValidBarcode: z.string().nullish(),
    mustSelectBundle: z.string().nullish(),
    mustSelectCrate: z.string().nullish(),
    ignoreSelectCrate: z.string().nullish(),
    emptyCrateSelected: z.string().nullish(),
    scannedPriceEncodedProductMoreThanOnceDialogOpen: z.string().nullish(),
    virtualBasketItem: z.string().nullish(),
    selfWeighing: z.string().nullish(),
    disableSurveys: z.string().nullish(),
    wantedUnknownBarcode: z.string().nullish(),
});

export type SessionFlags = z.infer<typeof SessionFlags>;

export const CustomerSessionInfo = z.object({
    customerNumber: z.int().nullish(),
    userGuid: z.string().nullish(),
    userHash: z.string().nullish(),
    labels: z.string().array().nullish(),
});

export type CustomerSessionInfo = z.infer<typeof CustomerSessionInfo>;

export const Session = z.object({
    guid: z.string(),
    appleOrGooglePayFlag: z.string().nullish(),
    availablePaymentMethods: z.string().array().nullish(),
    cartId: z.string().nullish(),
    /**
     * Which cart-image storage generation this session's images live in. Absent (and therefore `0`) on every
     * session created before the v2 rollout: those images are in the store's own `snapshotImages` day-buckets,
     * whereas `>= 2` means the flat `cartImages.images` collection and self-contained logical paths. Stamped once
     * at session creation, so a session is atomically one generation.
     */
    cartImageVersion: z.number().nullish(),
    createdOn: z.coerce.date().nullish(),
    customerCard: z.string().nullish(),
    employeeCard: z.string().nullish(),
    expectedWeight: z.number().nullish(),
    isDemoModeOn: z.boolean().nullish(),
    isMobile: z.boolean().nullish(),
    items: BasketItem.array().nullish(),
    loginHash: z.string().nullish(),
    loginTokenRaw: z.string().nullish(),
    loginType: z.string().nullish(),
    modifiedOn: z.coerce.date().nullish(),
    paymentRequests: Payment.array().nullish(),
    scaleTriggers: z.unknown().array().nullish(),
    sequence: z.number().nullish(),
    snapshotImages: SessionSnapshotImage.array().nullish(),
    /** @deprecated superseded by `snapshotImages`, whose `sessionStart` entries carry the same paths. */
    sessionStartImages: z.string().array().nullish(),
    shoppingListItems: z.unknown().array().nullish(),
    startedOn: z.coerce.date().nullish(),
    tickets: CustomerTicket.array().nullish(),
    type: z.string().nullish(),
    userGuid: z.string().nullish(),
    weightDeviations: z.unknown().array().nullish(),
    location: SessionLocation.nullish(),
    closedBy: z.string().nullish(),
    closedByReason: z.string().nullish(),
    closedByRoles: z.string().array().nullish(),
    endedOn: z.coerce.date().nullish(),
    /** @deprecated superseded by `snapshotImages`, whose `sessionEnd` entries carry the same paths. */
    sessionEndImages: z.string().array().nullish(),
    retain: RetainMarker.nullish(),
    bottleBundleChecks: BottleBundleCheck.array().nullish(),
    imageDetections: z.record(z.string(), SessionImageDetection).nullish(),
    imageDetectionConfirmations: z.record(z.string(), SessionImageDetectionConfirmation).nullish(),
    customer: CustomerSessionInfo.nullish(),
    customerAppPending: z.string().nullish(),
    customerAppDescription: CustomerAppDescription.nullish(),
    customerAppError: CustomerAppError.nullish(),
    /** The app whose registration failed with `customerAppError`, which outlives the pending scan. */
    customerAppErrorApp: z.string().nullish(),
    language: z.string().nullish(),
    externalPayment: ExternalPayment.nullish(),
    customerCardMetadata: z
        .object({
            type: z.string().nullish(),
            flags: z.string().array().nullish(),
        })
        .nullish(),
    weightWithoutScanAlerts: WeightWithoutScanModel.array().nullish(),
    customerInfo: SessionCustomerInfo.nullish(),
    manualCheck: SessionManualCheck.nullish(),
    preparationState: SessionPreparationState.optional(),
    flags: SessionFlags.nullish(),
    isInitialized: z.boolean().optional(),
    seenVoucherProgress: z.record(z.string(), z.any()).optional(),
    coupons: z.array(z.any()).optional(),
    sessionHash: z.string().optional(),
    deviceMetadata: SessionMonitorSessionDeviceMetadata.nullish(),
    customerAppLinkSucceeded: z.boolean().nullish(),
    appliedCustomerCardPoints: z.number().nullish(),
    availablePoints: z.number().nullish(),
    blockedPoints: z.number().nullish(),
    points: z.number().nullish(),
    totalPoints: z.number().nullish(),
});
export type Session = z.infer<typeof Session>;
